import { useState, useRef, WheelEvent } from "react";
import { MIN_ZOOM, MAX_ZOOM, ZOOM_FACTOR } from "../utils/timelineConstants";

interface UseTimelineZoomProps {
  initialZoom?: number;
  onZoomChange?: (zoom: number) => void;
}

export const useTimelineZoom = ({ initialZoom = 50, onZoomChange }: UseTimelineZoomProps = {}) => {
  const [zoom, setZoom] = useState(initialZoom);
  const [resizingSide, setResizingSide] = useState<"left" | "right" | null>(null);
  const [startPos, setStartPos] = useState(0);
  const [startZoom, setStartZoom] = useState(1);
  const wheelRafRef = useRef<number | null>(null);
  const wheelDeltaRef = useRef<number>(0);

  const updateZoom = (newZoom: number) => {
    const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));
    if (clampedZoom !== zoom) {
      setZoom(clampedZoom);
      onZoomChange?.(clampedZoom);
    }
  };

  /**
   * Handle wheel event for zooming with Ctrl/Cmd key
   */
  const handleWheel = (e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();

    wheelDeltaRef.current += e.deltaY;
    if (wheelRafRef.current !== null) return;

    wheelRafRef.current = requestAnimationFrame(() => {
      const delta = wheelDeltaRef.current;
      wheelDeltaRef.current = 0;
      wheelRafRef.current = null;

      const newZoom = delta > 0 ? zoom / ZOOM_FACTOR : zoom * ZOOM_FACTOR;
      updateZoom(newZoom);
    });
  };

  /**
   * Start resizing zoom with mouse drag
   */
  const handleResizeStart = (e: React.MouseEvent, side: "left" | "right") => {
    e.preventDefault();
    setResizingSide(side);
    setStartPos(side === "right" ? e.clientY : e.clientX);
    setStartZoom(zoom);
  };

  /**
   * Handle mouse move during resize
   */
  const handleMouseMove = (e: globalThis.MouseEvent) => {
    if (!resizingSide) return;

    const currentPos = resizingSide === "right" ? e.clientY : e.clientX;
    const delta = currentPos - startPos;
    const zoomChange = resizingSide === "right" ? -delta / 2 : -delta / 4;
    const newZoom = startZoom + zoomChange;

    updateZoom(newZoom);
    e.preventDefault();
  };

  /**
   * End resize
   */
  const handleMouseUp = () => {
    setResizingSide(null);
  };

  /**
   * Zoom in by a factor
   */
  const zoomIn = () => {
    updateZoom(zoom * ZOOM_FACTOR);
  };

  /**
   * Zoom out by a factor
   */
  const zoomOut = () => {
    updateZoom(zoom / ZOOM_FACTOR);
  };

  /**
   * Set zoom to specific value
   */
  const setZoomTo = (value: number) => {
    updateZoom(value);
  };

  /**
   * Get zoom percentage (0-1)
   */
  const getZoomPercentage = () => {
    return (zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM);
  };

  return {
    zoom,
    setZoom: setZoomTo,
    zoomIn,
    zoomOut,
    handleWheel,
    handleResizeStart,
    handleMouseMove,
    handleMouseUp,
    getZoomPercentage,
    isResizing: resizingSide !== null,
  };
};
